import React from "react";
import { Document, Page, Text, Font, View, StyleSheet } from "@react-pdf/renderer";
import { translate } from "./translation";
import { CommonStyles } from "./pdf-styles";
import { Header, P, Footer, Copyright, SVGRenderer, Column, Row } from "./components";
import moment from "moment";

const RobotoBold = require("./fonts/Roboto-Bold.ttf");
const RobotoRegular = require("./fonts/Roboto-Regular.ttf");

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: RobotoRegular,
      fontStyle: "normal",
      fontWeight: "normal"
    },
    {
      src: RobotoBold,
      fontStyle: "normal",
      fontWeight: "bold"
    }
  ]
});

const fontStyle = StyleSheet.create({
  bold: {
    fontFamily: "Roboto",
    fontWeight: "bold"
  },
  normal: {
    fontFamily: "Roboto",
    fontWeight: "normal"
  }
});

const paginateDataRows = (rows, rowsPerPage) => {
  const paginated = [];
  for (let i = 0; i < rows.length; i += rowsPerPage) {
    paginated.push(rows.slice(i, i + rowsPerPage));
  }
  return paginated;
};

const Legend = ({ legend }) => {
  let statusLegends = legend.map((status) => {
    return (
      <View 
        key={status.label} 
        style={{...CommonStyles.legendBlock, width: `${(1 / legend.length) * 100}%`, backgroundColor: status.color}}
      >
        <Text style={CommonStyles.legendText}>
          {status.label}
        </Text>
      </View>
    )
  })

  return (
    <View style={CommonStyles.legendContainer}>
      {statusLegends}
    </View>
  )
}

const StatusLabel = ({ color, textColor, children }) => {
  return (
    <View style={{backgroundColor: color, ...CommonStyles.statusLabel}}>
      <View style={{backgroundColor: textColor ?? color, opacity: 1, borderRadius: 2, height: 4, width: 4, marginRight: 3, marginBottom: 1}} />
      <Text style={{color: textColor ?? color, opacity: 1, ...CommonStyles.tenantRosterLabelText}}>{children}</Text>
    </View>
  )
}

const TenantRoster = ({ tenants, t }) => {
  const leaseTermColor = '#0088A7';
  const anchorColor = '#11B9A6';
  const categoryColor = '#FFFF00';
  const categoryTextColor = "#E9890B";

  const dataRows = tenants.map((tenant, idx) => {
    const color = tenant.statusColor.color;

    return (
      <Row key={idx} style={{height: 40, ...CommonStyles.tenantRosterRow}}>
        <Column style={{ width: "18.5%", ...CommonStyles.tenantRosterColumn}}>
          <Text style={CommonStyles.tenantRosterColumnText}>{tenant.isLeaseExpired ? t("LeaseRadar.pdf.available") : tenant.tenantName}</Text>
        </Column>
        <Column style={{ width: "8%", ...CommonStyles.tenantRosterColumn}}>
          <Text style={CommonStyles.tenantRosterColumnText}>{tenant.unit ? tenant.unit.split("#")[1] : ''}</Text>
        </Column>
        <Column style={{ width: "10%", ...CommonStyles.tenantRosterColumn}}>
          <Text style={CommonStyles.tenantRosterColumnText}>{tenant.leaseStart ? moment(tenant.leaseStart).format("MM/DD/YYYY") : ''}</Text>
        </Column>
        <Column style={{ width: "12%", ...CommonStyles.tenantRosterColumn}}>
          <Text style={CommonStyles.tenantRosterColumnText}>{tenant.leaseEnd ? moment(tenant.leaseEnd).format("MM/DD/YYYY") : ''}</Text>
        </Column>
        <Column style={{ width: "10%", ...CommonStyles.tenantRosterColumn}}>
          <Text style={CommonStyles.tenantRosterColumnText}>{tenant.vacateDate ? moment(tenant.vacateDate).format("MM/DD/YYYY") : ''}</Text>
        </Column>
        <Column style={{ width: "16.5%", ...CommonStyles.tenantRosterLabelColumn}}>
          {
            tenant.tenantCategoryDescription && (<StatusLabel color={categoryColor} textColor={categoryTextColor}>{tenant.tenantCategoryDescription}</StatusLabel>)
          }
        </Column>
        <Column style={{ width: "20%", ...CommonStyles.tenantRosterLabelColumn}}>
          <StatusLabel color={color}>{tenant.leaseExpirationFormatted}</StatusLabel>
          {
            tenant.leaseTerm && (
              <StatusLabel color={leaseTermColor}>{tenant.leaseTerm}</StatusLabel>
            )
          }
          {
            !!tenant.isAnchor && (
              <StatusLabel color={anchorColor}>{t("LeaseRadar.pdf.anchor")}</StatusLabel>
            )
          }
        </Column>
        <Column style={{ width: "5%", ...CommonStyles.tenantRosterColumn}}>
          <Text style={CommonStyles.tenantRosterColumnText}>{tenant?.squareFootage ?? 0} sq ft</Text>
        </Column>
      </Row>
    )
  });

  return (
    <View style={CommonStyles.tenantRoster}>
      <View fixed>
        <P style={CommonStyles.tenantRosterHeader}>{t("LeaseRadar.pdf.tenantRoster")}</P>
        <Row style={CommonStyles.tenantRosterRow}>
          <Column style={{width: "18.5%"}}>
            <P style={CommonStyles.tenantRosterColumnName}>{t("LeaseRadar.pdf.tenant")}</P>
          </Column>
          <Column style={{width: "8%"}}>
            <P style={CommonStyles.tenantRosterColumnName}>{t("LeaseRadar.pdf.spaceNumber")}</P>
          </Column>
          <Column style={{width: "10%"}}>
            <P style={CommonStyles.tenantRosterColumnName}>{t("LeaseRadar.pdf.leaseStart")}</P>
          </Column>
          <Column style={{width: "12%"}}>
            <P style={CommonStyles.tenantRosterColumnName}>{t("LeaseRadar.pdf.leaseExpiring")}</P>
          </Column>
          <Column style={{width: "10%"}}>
            <P style={CommonStyles.tenantRosterColumnName}>{t("LeaseRadar.pdf.vacateDate")}</P>
          </Column>
          <Column style={{width: "16.5%"}}>
            <P style={CommonStyles.tenantRosterColumnName}>{t("LeaseRadar.pdf.category")}</P>
          </Column>
          <Column style={{width: "20%"}}>
            <P style={CommonStyles.tenantRosterColumnName}>{t("LeaseRadar.pdf.status")}</P>
          </Column>
          <Column style={{width: "5%"}}>
            <P style={CommonStyles.tenantRosterColumnName}>SQFT</P>
          </Column>
        </Row>
      </View>
      <View>
        {dataRows}
      </View>
    </View>
  )
}

const LeaseRadarPDFTemplate = ({ data, language }) => {
  const t = translate(language);
  const rowsPerPage = 7;
  const paginatedRows = paginateDataRows(data.spaceData, rowsPerPage);

  return (
    <Document>
      {paginatedRows.map((tenantChunk, pageIndex) => (
        <Page key={pageIndex} style={{...CommonStyles.body, ...fontStyle.normal}}>
          <Header
            logo={data.company.logo}
            companyName={data.company.name}
            propertyName={
              data.property.displayName ||
                    data.property.crmName ||
                    data.property.name
            }
            address={[
              data.property.address1,
              data.property.city,
              data.property.state,
              data.property.postal
            ].join(", ")}
          >
            <P style={CommonStyles.gla}>
              {t("LeaseRadar.pdf.totalGLA")}:{" "}
              {Number(data.property.sqFootage).toLocaleString()}{" "}
              {t("LeaseRadar.pdf.squareFoot")}
            </P>
          </Header>
          <Legend legend={data.legend} />
          <SVGRenderer svg={data.svgString} hasPropertyChanged={data.hasPropertyChanged} />
          <TenantRoster tenants={tenantChunk} t={t} />
          <Footer>
            <Text
              style={CommonStyles.leftFooter}
              render={({ pageNumber }) =>
                t("LeaseRadar.pdf.footer", { pageNumber, totalPages: paginatedRows.length })
              }
            />
            <Text style={CommonStyles.rightFooter}>
              <Copyright>Copyright 2024</Copyright>
            </Text>
          </Footer>
        </Page>
      ))}
    </Document>
  )
}

export default LeaseRadarPDFTemplate;